import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { registryAPI } from '../../services/api';

// Images — dedicated home for the image lifecycle: browse the internal registry
// (repos/tags/versions) and build new images. Kept separate from Instances to avoid
// confusing "run a pod" with "build/manage images".
export default function ImagesPage() {
  const navigate = useNavigate();
  const [images, setImages] = useState([]);
  const [info, setInfo]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [imgRes, infoRes] = await Promise.all([
        registryAPI.listImages(),
        registryAPI.info().catch(() => ({ data: null })),
      ]);
      setImages(imgRes.data?.images || []);
      setInfo(infoRes.data);
      if (imgRes.data?.error) setError(`Registry: ${imgRes.data.error}`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load images');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = images.filter(i =>
    !filter || i.repository.toLowerCase().includes(filter.toLowerCase()));

  return (
    <div style={{ padding: '24px 32px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--ink)' }}>Images</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted)' }}>
            Build, version &amp; store container images in the internal registry.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={load} disabled={loading} className="btn btn-secondary btn-sm">
            {loading ? '…' : '⟳ Refresh'}
          </button>
          <button onClick={() => navigate('/compute/build')} className="btn btn-primary">
            🔨 Build Image
          </button>
        </div>
      </div>

      {/* Registry status chip */}
      {info && (
        <div style={{ fontSize: 12, color: 'var(--muted-2)', marginBottom: 16 }}>
          Registry: <code style={{ fontSize: 11 }}>{info.url}</code>{' '}
          <span style={{ color: info.reachable ? 'var(--color-success-600)' : 'var(--color-danger-500)' }}>
            {info.reachable ? '● reachable' : '● unreachable'}
          </span>
          {info.repository_count != null && ` · ${info.repository_count} repositories`}
        </div>
      )}

      {/* Filter */}
      <div style={{ marginBottom: 16 }}>
        <input placeholder="Filter by repository…" value={filter}
          onChange={e => setFilter(e.target.value)} className="input" style={{ maxWidth: 320 }} />
      </div>

      {error && <div className="banner warning" style={{ marginBottom: 16 }}>{error}</div>}

      {/* Table */}
      {!loading && filtered.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '64px 20px' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📦</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>No images yet</div>
          <div style={{ fontSize: 13, color: 'var(--muted-2)', marginBottom: 18 }}>
            Build one from a Dockerfile, or <code>docker push</code> to the registry.
          </div>
          <button onClick={() => navigate('/compute/build')} className="btn btn-primary">🔨 Build Image</button>
        </div>
      ) : (
        <div className="table-card">
          <table className="ds">
            <thead>
              <tr>{['Repository', 'Tags / Versions', 'Latest'].map(h => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {filtered.map(img => {
                const tags = img.tags || [];
                const latest = tags[tags.length - 1];
                return (
                  <tr key={img.repository}>
                    <td><code style={{ fontSize: 12, color: 'var(--ink)', fontWeight: 600 }}>{img.repository}</code></td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {tags.map(t => (
                          <span key={t} style={{ fontSize: 11, background: 'var(--surface-2)', color: 'var(--ink-2)',
                            padding: '2px 8px', borderRadius: 10 }}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--muted-2)', fontFamily: 'monospace' }}>
                      {latest ? `${img.repository}:${latest}` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
